import {
    AssetCategoryParameterChild,
    AssetCategoryParameterType,
} from '@kcftech/prometheustypescriptapiclient';
import { AssetParameterFormData } from 'pages/Settings/AssetType/parameter_form/types';
import { RegisterOptions } from 'react-hook-form';
import styled from 'styled-components';
import { isValidNumber } from 'utils/number';
import { hasNonEmptyValue } from 'utils/string';

export const defaultValueMaxLength = 300;
export const validValueMaxLength = 300;

export const MinMaxInputContainer = styled.div`
    display: flex;
    gap: 2rem;
`;

export const buildCategoryParameter = (formData: AssetParameterFormData) =>
    new AssetCategoryParameterChild({
        Code: formData.code.trim(),
        Name: formData.name.trim(),
        CategoryParameterType: formData.type,
        MinValue: formData.minValue?.trim() ? Number(formData.minValue) : undefined,
        MinValueInclusive: formData.minValueInclusive ?? undefined,
        MaxValue: formData.maxValue?.trim() ? Number(formData.maxValue) : undefined,
        MaxValueInclusive: formData.maxValueInclusive ?? undefined,
        DefaultValue: formData.defaultValue?.trim() ?? undefined,
        ValidValues: formData.validValues ?? undefined,
    });

export const baseDefaultValueRules: RegisterOptions<
    AssetParameterFormData,
    'defaultValue'
> = {
    maxLength: {
        value: defaultValueMaxLength,
        message: `Default Value cannot exceed ${defaultValueMaxLength} characters`,
    },
    validate: () => undefined,
};

const isValidNumericValue = (
    value: string | null,
    type: AssetCategoryParameterType.Integer | AssetCategoryParameterType.Decimal
) =>
    value !== null &&
    isValidNumber(value) &&
    (type !== AssetCategoryParameterType.Integer || Number.isInteger(Number(value)));

export type GetNumericDefaultValueRulesOptions = Readonly<{
    type: AssetCategoryParameterType.Integer | AssetCategoryParameterType.Decimal;
}> &
    Pick<
        AssetParameterFormData,
        'minValue' | 'maxValue' | 'minValueInclusive' | 'maxValueInclusive'
    >;

export const getNumericDefaultValueRules = ({
    type,
    minValue,
    maxValue,
    minValueInclusive,
    maxValueInclusive,
}: GetNumericDefaultValueRulesOptions): RegisterOptions<
    AssetParameterFormData,
    'defaultValue'
> => ({
    ...baseDefaultValueRules,
    validate: (value) => {
        if (!value) {
            return undefined;
        }

        if (!isValidNumericValue(value, type)) {
            return type === AssetCategoryParameterType.Decimal
                ? 'Default Value must be a valid number'
                : 'Default Value must be a valid integer';
        }

        const parsedValue = Number(value);

        if (hasNonEmptyValue(minValue) && isValidNumericValue(minValue, type)) {
            const isMinValueInclusive =
                type === AssetCategoryParameterType.Integer || minValueInclusive;

            if (isValidNumericValue(minValue, type)) {
                if (isMinValueInclusive && parsedValue < Number(minValue)) {
                    return 'Default Value must be greater than or equal to Min Value';
                }
                if (!isMinValueInclusive && parsedValue <= Number(minValue)) {
                    return 'Default Value must be greater than Min Value';
                }
            }
        }

        if (hasNonEmptyValue(maxValue) && isValidNumericValue(maxValue, type)) {
            const isMaxValueInclusive =
                type === AssetCategoryParameterType.Integer || maxValueInclusive;

            if (isValidNumericValue(maxValue, type)) {
                if (isMaxValueInclusive && parsedValue > Number(maxValue)) {
                    return 'Default value must be less than or equal to Max Value';
                }
                if (!isMaxValueInclusive && parsedValue >= Number(maxValue)) {
                    return 'Default value must be less than Max Value';
                }
            }
        }

        return undefined;
    },
});

export const getMinValueRules = (
    type: AssetCategoryParameterType.Integer | AssetCategoryParameterType.Decimal,
    maxValue: string | null
): RegisterOptions<AssetParameterFormData, 'minValue'> => ({
    validate: (value) => {
        if (!value) {
            return undefined;
        }

        if (!isValidNumericValue(value, type)) {
            return type === AssetCategoryParameterType.Integer
                ? 'Min Value must be a valid integer'
                : 'Min Value must be a valid number';
        }

        if (
            hasNonEmptyValue(maxValue) &&
            isValidNumericValue(maxValue, type) &&
            Number(value) > Number(maxValue)
        ) {
            return 'Min Value must be less than or equal to Max Value';
        }

        return undefined;
    },
});

export const getMaxValueRules = (
    type: AssetCategoryParameterType.Integer | AssetCategoryParameterType.Decimal,
    minValue: string | null
): RegisterOptions<AssetParameterFormData, 'maxValue'> => ({
    validate: (value) => {
        if (!value) {
            return undefined;
        }

        if (!isValidNumericValue(value, type)) {
            return type === AssetCategoryParameterType.Integer
                ? 'Max Value must be a valid integer'
                : 'Max Value must be a valid number';
        }

        if (
            hasNonEmptyValue(minValue) &&
            isValidNumericValue(minValue, type) &&
            Number(value) < Number(minValue)
        ) {
            return 'Max Value must be greater than or equal to Min Value';
        }

        return undefined;
    },
});
